import React, { useState, useMemo, useEffect } from 'react'
import dayjs, { Dayjs } from 'dayjs'
import {
  Modal,
  Form,
  Row,
  Col,
  DatePicker,
  InputNumber,
  Select,
  Checkbox,
  Input,
  Divider,
  Button,
  Space,
  Typography,
  Radio,
  Tooltip,
  message,
} from 'antd'
import {
  ProjectOutlined,
  CalendarOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
  InfoCircleOutlined,
  RedoOutlined,
} from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import { Project } from '../../stores/ProjectsStore'
import { REGULAR_EVENT_TYPES } from './constants'
import { ExtendedEvent, RecurrenceRule } from './types'
import { toLocalDate } from '../../helpers/dateConverters'
import { Employee } from '../../models/Employee'

const { Option } = Select
const { TextArea } = Input
const { Text } = Typography

interface Props {
  open: boolean
  onCancel: () => void
  onSubmit: (values: any) => void
  loading: boolean
  eventType: 'regular' | 'project' | 'crm'
  setEventType: (t: 'regular' | 'project' | 'crm') => void
  users: Employee[]
  projects: Project[]
  initialDate: Date | null
  initialEvent?: ExtendedEvent | null
  editMode?: 'single' | 'series' | null
}

const defaultRecurrence: RecurrenceRule = {
  pattern: 'none',
  interval: 1,
  byWeekday: [1],
  end: { type: 'never' },
}
const REGULAR_TYPES_FOR_CREATION = REGULAR_EVENT_TYPES.filter(e => e.type !== 'Birthday')
const durationOptions = Array.from({ length: 23 }, (_, i) => (i + 1) / 2)

const AddEventModal: React.FC<Props> = ({
  open,
  onCancel,
  onSubmit,
  loading,
  eventType,
  setEventType,
  users,
  projects,
  initialDate,
  initialEvent,
  editMode,
}) => {
  const { t } = useTranslation()
  const [form] = Form.useForm()
  const [rec, setRec] = useState<RecurrenceRule>(defaultRecurrence)
  const [titleTouched, setTitleTouched] = useState(false)

  const userMap = useMemo(() => {
    const map = new Map<string, Employee>()
    users.forEach(u => map.set(String(u.id), u))
    return map
  }, [users])

  const generateDefaultTitle = (projectId?: any, participantIds?: string[]) => {
    if (!projectId) return ''
    const project = projects.find(p => p.id === projectId)
    const projectName = project?.title || project?.name || ''
    if (!projectName) return ''
    const participantNames: string[] = []
    if (participantIds && participantIds.length > 0) {
      participantIds.forEach(id => {
        const user = userMap.get(String(id))
        if (user) {
          const name = `${user.firstName} ${user.lastName}`.trim()
          if (name) participantNames.push(name)
        }
      })
    }
    if (participantNames.length > 0) {
      return `${projectName}, ${participantNames.join(', ')}`
    }
    return projectName
  }

  const setDefaultTitle = () => {
    if (eventType === 'project') {
      const projectId = form.getFieldValue('projectId')
      const participants = form.getFieldValue('participants') || []
      const defaultTitle = generateDefaultTitle(projectId, participants)
      form.setFieldsValue({ title: defaultTitle })
      setTitleTouched(false)
    }
  }

  const handleProjectChange = (value: any) => {
    if (!titleTouched && eventType === 'project' && !editMode) {
      const participants = form.getFieldValue('participants') || []
      const defaultTitle = generateDefaultTitle(value, participants)
      form.setFieldsValue({ title: defaultTitle })
    }
  }

  const handleParticipantsChange = (values: string[]) => {
    if (!titleTouched && eventType === 'project' && !editMode) {
      const projectId = form.getFieldValue('projectId')
      const defaultTitle = generateDefaultTitle(projectId, values)
      form.setFieldsValue({ title: defaultTitle })
    }
  }

  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTitleTouched(true)
  }

  useEffect(() => {
    if (!open) {
      form.resetFields()
      setRec(defaultRecurrence)
      setTitleTouched(false)
      return
    }
    if (initialEvent) {
      setEventType(initialEvent.type)
      const base = {
        description: initialEvent.description,
        needsApproval: initialEvent.needsApproval,
      }
      if (initialEvent.type === 'regular') {
        form.setFieldsValue({
          ...base,
          title: initialEvent.title,
          date: dayjs(initialEvent.start),
          endDate: dayjs(initialEvent.end),
          eventType: initialEvent.eventType,
        })
      } else {
        const startDateTime = initialEvent.type === 'project' ? dayjs(toLocalDate(initialEvent.start)) : dayjs(initialEvent.start)
        const endDateTime = initialEvent.type === 'project' ? dayjs(toLocalDate(initialEvent.end)) : dayjs(initialEvent.end)
        const duration = endDateTime.diff(startDateTime, 'hour', true)
        const participants = initialEvent.participants?.map(p => String(p)) || []
        form.setFieldsValue({
          ...base,
          title: initialEvent.title || '',
          projectId: initialEvent.projectId,
          participants: participants,
          dateTime: startDateTime,
          duration: Math.max(0.5, duration),
        })
        setTitleTouched(true)
      }
      if (editMode === 'series' || !initialEvent.parentEventId)
        setRec(initialEvent.recurrence ?? defaultRecurrence)
      else setRec(defaultRecurrence)
      return
    }
    if (initialDate) {
      form.resetFields()
      setRec(defaultRecurrence)
      setTitleTouched(false)
      if (eventType === 'regular') form.setFieldsValue({ date: dayjs(initialDate) })
      else form.setFieldsValue({ dateTime: dayjs(initialDate), duration: 0.5 })
    }
  }, [open, initialDate, initialEvent, eventType, setEventType, form, projects, editMode])

  useEffect(() => {
    if (open && !initialEvent && eventType === 'project') {
      const baseDate = initialDate ? dayjs(initialDate) : dayjs()
      if (!form.getFieldValue('dateTime'))
        form.setFieldsValue({
          dateTime: baseDate,
          duration: 0.5,
        })
      if (!titleTouched) {
        const projectId = form.getFieldValue('projectId')
        const participants = form.getFieldValue('participants') || []
        if (projectId) {
          const defaultTitle = generateDefaultTitle(projectId, participants)
          form.setFieldsValue({ title: defaultTitle })
        }
      }
    }
  }, [eventType, open, initialDate, initialEvent, form, titleTouched])

  const userOpts = useMemo(
    () =>
      users
        .filter(u => u.firstName || u.lastName)
        .map(u => ({ label: `${u.firstName} ${u.lastName}`.trim(), value: String(u.id) }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })),
    [users],
  )

  const projOpts = useMemo(() => {
    const sorted = projects
      .map(p => ({ label: p.title || p.name, value: p.id }))
      .sort((a, b) => {
        const labelA = String(a.label || '').trim().toLowerCase()
        const labelB = String(b.label || '').trim().toLowerCase()
        if (labelA < labelB) return -1
        if (labelA > labelB) return 1
        return 0
      })
    console.log('Sorted projects:', sorted.map(p => p.label))
    return sorted
  }, [projects])

  const patternKey = (p: RecurrenceRule['pattern']): string =>
    typeof p === 'number' ? ['none', 'daily', 'weekly', 'monthly', 'yearly'][p] : p

  const WEEKDAY = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
  const weekDayOpts = WEEKDAY.map((w, idx) => ({ label: t(w), value: idx }))

  const recSummary = useMemo(() => {
    if (rec.pattern === 'none') return t('DoesNotRepeat')
    const label = t(patternKey(rec.pattern))
    const every =
      rec.interval > 1
        ? t('EveryN', { interval: rec.interval, unit: label.toLowerCase() })
        : `${t('Every')} ${label.toLowerCase()}`
    let extra = ''
    if (rec.pattern === 'weekly' && rec.byWeekday?.length)
      extra = ' (' + rec.byWeekday.sort().map(d => t(WEEKDAY[d])).join(', ') + ')'
    const end =
      rec.end.type === 'never'
        ? t('Forever')
        : rec.end.type === 'untilDate'
          ? `${t('Until')} ${dayjs(rec.end.date!).format('YYYY-MM-DD')}`
          : `${t('For')} ${rec.end.count}×`
    return `${every}${extra} • ${end}`
  }, [rec, t])

  const todayDisabled = (d: dayjs.Dayjs) => d && d.isBefore(dayjs().startOf('day'))

  const handleFinish = (vals: any) => {
    let start: Dayjs
    let end: Dayjs
    if (eventType === 'regular') {
      start = vals.date
      end = vals.endDate ? vals.endDate : vals.date
    } else {
      start = vals.dateTime
      end = start.clone().add(vals.duration || 0.5, 'hour')
    }
    if (start.isBefore(dayjs().startOf('day'))) {
      message.error(t('CannotCreatePast'))
      return
    }
    const participants = vals.participants ? (vals.participants as any[]).map(p => String(p)) : []
    const submitData = {
      ...vals,
      participants,
      start: start.toDate(),
      end: end.toDate(),
      date: start,
      endDate: end,
      dateTime: start,
      originalEventId: initialEvent?.id,
      parentEventId: initialEvent?.parentEventId,
      masterId: initialEvent?.parentEventId || initialEvent?.id,
      recurrence: editMode === 'series' || !editMode ? rec : undefined,
    }
    onSubmit(submitData)
  }

  return (
    <Modal
      open={open}
      destroyOnClose
      width={700}
      footer={null}
      onCancel={() => {
        form.resetFields()
        setRec(defaultRecurrence)
        setTitleTouched(false)
        onCancel()
      }}
      title={
        <Space>
          <PlusOutlined />
          {editMode === 'single' ? t('EditSingleOccurrence') : editMode === 'series' ? t('EditSeries') : t('AddEvent')}
        </Space>
      }
    >
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Form.Item label={t('EventCategory')}>
          <Select size="large" value={eventType} onChange={v => setEventType(v as any)} disabled={Boolean(editMode)}>
            <Option value="regular">
              <CalendarOutlined /> {t('Company')}
            </Option>
            <Option value="project">
              <ProjectOutlined /> {t('Project')}
            </Option>
          </Select>
        </Form.Item>

        {eventType === 'regular' && (
          <>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item name="date" label={t('StartDate')} rules={[{ required: true, message: t('Required') }]}>
                  <DatePicker style={{ width: '100%' }} disabledDate={todayDisabled} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="endDate"
                  label={t('EndDate')}
                  dependencies={['date']}
                  rules={[
                    {
                      validator(_, value) {
                        const startDate = form.getFieldValue('date');
                        if (!value || !startDate || !dayjs(value).isBefore(startDate, 'day')) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error(t('EndDateMustBeAfterStart')));
                      },
                    },
                  ]}
                >
                  <DatePicker style={{ width: '100%' }} disabledDate={todayDisabled} />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item name="eventType" label={t('EventType')} rules={[{ required: true, message: t('Required') }]}>
              <Select placeholder={t('SelectType')}>
                {REGULAR_TYPES_FOR_CREATION.map(e => (
                  <Option key={e.type} value={e.type}>
                    {e.icon} {t(e.type)}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </>
        )}

        {eventType === 'project' && (
          <>
            <Row gutter={16}>
              <Col span={14}>
                <Form.Item name="dateTime" label={t('Start')} rules={[{ required: true, message: t('Required') }]}>
                  <DatePicker
                    showTime={{ format: 'HH:mm' }}
                    style={{ width: '100%' }}
                    format="YYYY-MM-DD HH:mm"
                    disabledDate={todayDisabled}
                  />
                </Form.Item>
              </Col>
              <Col span={10}>
                <Form.Item name="duration" label={t('DurationH')} rules={[{ required: true, message: t('Required') }]}>
                  <Select style={{ width: '100%' }}>
                    {durationOptions.map(d => (
                      <Option key={d} value={d}>
                        {d} {t('HoursAbbr')}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item name="projectId" label={t('Project')} rules={[{ required: true, message: t('Required') }]}>
              <Select
                placeholder={t('Select')}
                options={projOpts}
                onChange={handleProjectChange}
              />
            </Form.Item>

            <Form.Item
              name="title"
              label={
                <Space>
                  {t('EventTitle')}
                  <Button
                    type="link"
                    size="small"
                    icon={<RedoOutlined />}
                    onClick={setDefaultTitle}
                    style={{ padding: '0 4px' }}
                  >
                    {t('SetDefaultTitle') || 'Set Default Title'}
                  </Button>
                </Space>
              }
              extra={!editMode ? (t('AutoGeneratedFromProjectAndParticipants') || 'Auto-generated from project and participants') : null}
            >
              <Input
                placeholder={t('EventTitle')}
                maxLength={200}
                onChange={handleTitleChange}
              />
            </Form.Item>

            <Form.Item name="participants" label={t('Participants')}>
              <Select
                mode="multiple"
                placeholder={t('Select')}
                options={userOpts}
                onChange={handleParticipantsChange}
              />
            </Form.Item>

            <Form.Item name="description" label={t('Description')}>
              <TextArea rows={3} />
            </Form.Item>

            <Form.Item name="needsApproval" valuePropName="checked">
              <Checkbox>
                <ExclamationCircleOutlined /> {t('NeedsApproval')}
              </Checkbox>
            </Form.Item>

            {(editMode === 'series' || !editMode) && (
              <>
                <Divider orientation="left">
                  {t('RecurrenceSettings')}{' '}
                  <Tooltip title={t('RecurrenceHint')}>
                    <InfoCircleOutlined />
                  </Tooltip>
                </Divider>
                <Row gutter={16}>
                  <Col span={10}>
                    <Radio.Group value={rec.pattern} onChange={e => setRec(prev => ({ ...prev, pattern: e.target.value }))}>
                      <Space direction="vertical">
                        <Radio value="none">{t('DoesNotRepeat')}</Radio>
                        <Radio value="daily">{t('Daily')}</Radio>
                        <Radio value="weekly">{t('Weekly')}</Radio>
                        <Radio value="monthly">{t('Monthly')}</Radio>
                        <Radio value="yearly">{t('Yearly')}</Radio>
                      </Space>
                    </Radio.Group>
                  </Col>
                  {rec.pattern !== 'none' && (
                    <Col span={14}>
                      <Form.Item label={t('Every')}>
                        <Space>
                          <InputNumber
                            min={1}
                            max={365}
                            value={rec.interval}
                            onChange={v => setRec(prev => ({ ...prev, interval: v ?? 1 }))}
                          />
                          <span>{t(typeof rec.pattern === 'string' ? rec.pattern : '')}</span>
                        </Space>
                      </Form.Item>
                      {rec.pattern === 'weekly' && (
                        <Form.Item label={t('OnDays')}>
                          <Checkbox.Group
                            options={weekDayOpts}
                            value={rec.byWeekday}
                            onChange={v => setRec(prev => ({ ...prev, byWeekday: v as number[] }))}
                          />
                        </Form.Item>
                      )}
                      <Divider orientation="left" plain>
                        {t('Ends')}
                      </Divider>
                      <Radio.Group
                        value={rec.end.type}
                        onChange={e => {
                          const type = e.target.value as 'never' | 'untilDate' | 'after'
                          if (type === 'untilDate')
                            setRec(prev => ({ ...prev, end: { type, date: dayjs().add(1, 'month') } }))
                          else if (type === 'after') setRec(prev => ({ ...prev, end: { type, count: 10 } }))
                          else setRec(prev => ({ ...prev, end: { type } }))
                        }}
                      >
                        <Space direction="vertical">
                          <Radio value="never">{t('Never')}</Radio>
                          <Radio value="untilDate">
                            {t('On')}
                            {rec.end.type === 'untilDate' && (
                              <DatePicker
                                size="small"
                                value={rec.end.date as any}
                                style={{ marginLeft: 8 }}
                                disabledDate={todayDisabled}
                                onChange={d => setRec(prev => ({ ...prev, end: { type: 'untilDate', date: d } }))}
                              />
                            )}
                          </Radio>
                          <Radio value="after">
                            {t('After')}
                            {rec.end.type === 'after' && (
                              <InputNumber
                                size="small"
                                min={1}
                                max={100}
                                value={rec.end.count}
                                onChange={v => setRec(prev => ({ ...prev, end: { type: 'after', count: v ?? 1 } }))}
                                style={{ marginLeft: 8 }}
                              />
                            )}{' '}
                            {t('Occurrences')}
                          </Radio>
                        </Space>
                      </Radio.Group>
                    </Col>
                  )}
                </Row>
                {rec.pattern !== 'none' && (
                  <div style={{ background: '#e6f7ff', padding: 12, borderRadius: 6, marginTop: 12 }}>
                    <Text type="secondary">{recSummary}</Text>
                  </div>
                )}
              </>
            )}

            {editMode === 'single' && (
              <div style={{ background: '#fff7e6', padding: 12, borderRadius: 6, marginTop: 12 }}>
                <Text type="secondary">{t('EditingSingleOccurrence')}</Text>
              </div>
            )}
          </>
        )}

        <Form.Item>
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={onCancel}>{t('Cancel')}</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              disabled={eventType === 'project' && rec.pattern === 'weekly' && rec.byWeekday!.length === 0}
            >
              {editMode ? t('Save') : t('Create')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default AddEventModal
